import React, { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNotify } from 'react-admin';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { apiFetch, BillDiffRow, DropoffBatch, formatDate, money, monthName, PartnerMonthDetail } from './api';

/**
 * One partner's month (T-082). Everyone: the cases we sent and which of their
 * uploaded bills each is on, plus "sent, not billed yet". A partner that
 * invoices from the portal (Brampton): the batches it marked, priced from the
 * ledger, and each uploaded bill compared with them line by line.
 */
export const PartnerMonthPage = () => {
  const { partnerId, month } = useParams<{
    partnerId: string;
    month: string;
  }>();
  const notify = useNotify();
  const [data, setData] = useState<PartnerMonthDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`partner-invoices/months/${partnerId}/${month}`));
    } catch {
      notify('Failed to load this partner month', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [partnerId, month, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) return null;
  const { partner, portal } = data;

  return (
    <Box p={3} display="grid" gap={3}>
      <Breadcrumbs>
        <Link component={RouterLink} to="/partner-reconciliation">
          Partner Invoices
        </Link>
        <Link component={RouterLink} to="/partner-reconciliation?view=months">
          By partner &amp; month
        </Link>
        <Typography color="text.primary">
          {partner.name} · {monthName(data.month)}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
        <Typography variant="h5">
          {partner.name} · {monthName(data.month)}
        </Typography>
        {portal ? (
          <Chip size="small" variant="outlined" label="Invoices from the portal ledger" />
        ) : (
          <Chip size="small" variant="outlined" label="No rate card" />
        )}
      </Box>

      <Stats data={data} />

      {portal && <PortalSection data={data} portal={portal} />}

      <Section title="On their uploaded bills">
        {data.invoices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No bill uploaded for this month yet. Upload it from Partner Invoices and it lands here.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Cases in this month</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.invoices.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell>
                      <Link component={RouterLink} to={`/partner-reconciliation/${inv.id}`}>
                        {inv.invoice_number || 'Open invoice'}
                      </Link>
                    </TableCell>
                    <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                    <TableCell>
                      {inv.period_start || inv.period_end
                        ? `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}`
                        : '—'}
                    </TableCell>
                    <TableCell align="right">{inv.matched_in_month}</TableCell>
                    <TableCell align="right">{money(inv.extracted_total)}</TableCell>
                    <TableCell>{inv.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {!portal && (
        <Section title={`Sent in ${monthName(data.month).split(' ')[0]}, not on any bill yet`}>
          {data.not_billed.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Every case sent this month is on one of their bills.
            </Typography>
          ) : (
            <>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {data.not_billed.map((c) => (
                  <Chip
                    key={c.id}
                    size="small"
                    variant="outlined"
                    label={`#${c.id} ${c.name}`}
                    component={RouterLink}
                    to={`/funerals/${c.id}/show`}
                    clickable
                  />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary" mt={1}>
                These are most likely on their next bill; the page can only say they are not billed <i>yet</i>.
              </Typography>
            </>
          )}
        </Section>
      )}

      <Section title="Every case sent this month">
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Case</TableCell>
                <TableCell>{portal ? 'Arrived' : 'Sent'}</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Billed on</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.cases.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link component={RouterLink} to={`/funerals/${c.id}/show`}>
                      #{c.id} {c.name}
                    </Link>
                  </TableCell>
                  <TableCell>{formatDate(c.date)}</TableCell>
                  <TableCell>{c.roles.join(', ')}</TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {portal &&
                        (portal.batches.some((b) => b.id === c.dropoff_invoice_id) ? (
                          <Chip size="small" color="success" variant="outlined" label="Marked in portal" />
                        ) : (
                          <Chip size="small" variant="outlined" label="Not marked yet" />
                        ))}
                      {c.billed_on.map((b) => (
                        <Chip
                          key={b.invoice_id}
                          size="small"
                          variant="outlined"
                          color={b.reconciled ? 'success' : 'default'}
                          label={`${b.invoice_number || 'bill'}${b.reconciled ? ' ✓' : ''}`}
                          component={RouterLink}
                          to={`/partner-reconciliation/${b.invoice_id}`}
                          clickable
                        />
                      ))}
                      {!portal && c.billed_on.length === 0 && (
                        <Chip size="small" color="warning" variant="outlined" label="Not billed yet" />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </Box>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box display="grid" gap={1.25}>
    <Typography variant="h6">{title}</Typography>
    {children}
  </Box>
);

const Stats = ({ data }: { data: PartnerMonthDetail }) => {
  const sent = data.cases.length;
  const stat = (value: React.ReactNode, label: string, warn = false) => (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1, minWidth: 150 }}>
      <Typography
        variant="h5"
        color={warn ? 'warning.main' : 'text.primary'}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
  if (data.portal) {
    const marked = sent - data.portal.not_marked;
    const total = data.portal.batches.reduce((s, b) => s + b.total, 0);
    return (
      <Stack direction="row" flexWrap="wrap" gap={1.25}>
        {stat(sent, 'cases arrived')}
        {stat(marked, 'marked invoiced in the portal')}
        {stat(data.portal.not_marked, 'not marked yet', data.portal.not_marked > 0)}
        {stat(marked ? money(total) : '—', 'ledger total incl. HST')}
      </Stack>
    );
  }
  const billed = sent - data.not_billed.length;
  return (
    <Stack direction="row" flexWrap="wrap" gap={1.25}>
      {stat(sent, 'cases sent')}
      {stat(billed, 'on their uploaded bills')}
      {stat(data.not_billed.length, 'not billed yet', data.not_billed.length > 0)}
      {stat('—', 'expected $ (no rate card)')}
    </Stack>
  );
};

const PortalSection = ({
  data,
  portal,
}: {
  data: PartnerMonthDetail;
  portal: NonNullable<PartnerMonthDetail['portal']>;
}) => {
  return (
    <>
      <Section title="Marked invoiced in the portal">
        {portal.batches.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing marked for {monthName(data.month)} yet. The facility invoices a month from its drop-off ledger; the
            lines and totals are saved here when it clicks "Mark as invoiced".
          </Typography>
        ) : (
          portal.batches.map((b) => <BatchCard key={b.id} batch={b} />)
        )}
        {portal.not_marked > 0 && portal.batches.length > 0 && (
          <Alert severity="warning">
            {portal.not_marked} case{portal.not_marked === 1 ? '' : 's'} arrived this month but{' '}
            {portal.not_marked === 1 ? 'is' : 'are'} not on a portal invoice yet.
          </Alert>
        )}
      </Section>

      {portal.comparisons.map((c) => (
        <Section
          key={c.invoice_id}
          title={`Their bill ${c.invoice_number || ''} vs the ledger (${c.covered_months.map(monthName).join(', ')})`}
        >
          {c.batches === 0 && (
            <Alert severity="info">
              Nothing is marked invoiced in the portal for {c.covered_months.map(monthName).join(', ')}, so the ledger
              side is empty. It fills in once the facility marks those months.
            </Alert>
          )}
          <DiffTable rows={c.rows} theirs={c.theirs} ledger={c.ledger} />
          <Typography variant="body2" color="text.secondary">
            Ledger side: {c.ledger_cases} case{c.ledger_cases === 1 ? '' : 's'} in {c.batches} portal batch
            {c.batches === 1 ? '' : 'es'}, priced when marked. Their quantities are their amount ÷ the rate.
          </Typography>
        </Section>
      ))}
    </>
  );
};

const BatchCard = ({ batch }: { batch: DropoffBatch }) => {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'grid', gap: 1 }}>
      <Typography variant="subtitle1" fontWeight="bold">
        Marked {formatDate(batch.marked_at)} · {batch.case_count} case
        {batch.case_count === 1 ? '' : 's'} · {money(batch.total)} incl. HST
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Product (SKU)</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batch.lines.map((l) => (
              <Fragment key={l.key}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => setOpen(open === l.key ? null : l.key)}
                      aria-label="Show cases"
                    >
                      {open === l.key ? (
                        <KeyboardArrowUpIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {l.product}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {l.sku}
                    </Typography>
                    {l.taxExempt && <Chip size="small" label="HST exempt" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell align="right">{l.qty ?? ''}</TableCell>
                  <TableCell align="right">{l.rate == null ? 'at cost' : money(l.rate)}</TableCell>
                  <TableCell align="right">{money(l.amount)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={5}
                    sx={{
                      py: 0,
                      borderBottom: open === l.key ? undefined : 'none',
                    }}
                  >
                    <Collapse in={open === l.key} unmountOnExit>
                      <Box py={1} display="flex" flexWrap="wrap" gap={0.75}>
                        {(l.cases || []).map((c) => (
                          <Chip
                            key={c.id}
                            size="small"
                            variant="outlined"
                            label={`#${c.id} ${c.name}${c.note ? ` ${c.note}` : ''}`}
                            component={RouterLink}
                            to={`/funerals/${c.id}/show`}
                            clickable
                          />
                        ))}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
            <TableRow>
              <TableCell colSpan={4} align="right">
                HST 13% on {money(batch.taxable_subtotal)}
              </TableCell>
              <TableCell align="right">{money(batch.hst)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const signed = (n: number) => (Math.abs(n) < 0.005 ? '—' : `${n > 0 ? '+' : '−'}${money(Math.abs(n))}`);

const DiffTable = ({
  rows,
  theirs,
  ledger,
}: {
  rows: BillDiffRow[];
  theirs: { subtotal: number | null; tax: number | null; total: number | null };
  ledger: { subtotal: number; tax: number; total: number };
}) => {
  const diffTotal = theirs.total != null ? theirs.total - ledger.total : null;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Line</TableCell>
            <TableCell align="right">Their qty</TableCell>
            <TableCell align="right">Their $</TableCell>
            <TableCell align="right">Ledger qty</TableCell>
            <TableCell align="right">Ledger $</TableCell>
            <TableCell align="right">Difference</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const off = Math.abs(r.difference) >= 0.005;
            return (
              <TableRow key={r.key} sx={off ? { bgcolor: '#fffbeb' } : undefined}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {r.product}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {r.sku || 'not in the ledger'}
                    {r.tax_exempt ? ' · HST exempt' : ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">{r.their_qty ?? ''}</TableCell>
                <TableCell align="right">{money(r.their_amount)}</TableCell>
                <TableCell align="right">{r.ledger_qty ?? ''}</TableCell>
                <TableCell align="right">{money(r.ledger_amount)}</TableCell>
                <TableCell align="right">
                  {off ? (
                    <b>{signed(r.difference)}</b>
                  ) : (
                    <Chip size="small" color="success" variant="outlined" label="OK" />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow>
            <TableCell>HST</TableCell>
            <TableCell />
            <TableCell align="right">{money(theirs.tax)}</TableCell>
            <TableCell />
            <TableCell align="right">{money(ledger.tax)}</TableCell>
            <TableCell align="right">{theirs.tax != null ? signed(theirs.tax - ledger.tax) : ''}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <b>Total incl. HST</b>
            </TableCell>
            <TableCell />
            <TableCell align="right">
              <b>{money(theirs.total)}</b>
            </TableCell>
            <TableCell />
            <TableCell align="right">
              <b>{money(ledger.total)}</b>
            </TableCell>
            <TableCell align="right">
              {diffTotal != null && (
                <Box display="inline-flex" alignItems="center" gap={1}>
                  <b>{signed(diffTotal)}</b>
                  {diffTotal > 0.005 && <Chip size="small" color="warning" label="Overbilled" />}
                  {diffTotal < -0.005 && <Chip size="small" color="info" label="Underbilled" />}
                </Box>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PartnerMonthPage;
